import React from 'react';
import { observer } from 'mobx-react';
import { autorun, computed, observable, toJS } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { AppUI, ExerciseUI } from '@seedlang/state';
import WorksheetGridItem from 'components/worksheet/worksheet_grid_item';
import autobind from 'autobind-decorator';
import WorksheetFilter from 'components/worksheet/worksheet_filter';
import { isBlank } from '@seedlang/utils';
import Paginator from 'components/paginator';
import { keys, some, xor } from 'lodash';
import { isPresent } from '@seedlang/utils/src';
import Text from 'components/text';
import Spinner from 'components/spinner';
import { deserialize } from 'serializr';
import { GridItemWorksheet } from '@seedlang/models';
import { Constants } from '@seedlang/constants';
import { Link } from 'react-router';
import Stack from 'components/layout/stack';

const Wrapper = styled.div`
  padding-top: ${props => props.paddingTop};
`;

const Section = styled.div`
  border-radius: 10px;
  margin-bottom: 15px;
  width: 100%;
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: bold;
  padding: 10px 10px 10px 0;
  border-radius: 10px;
  cursor: pointer;
  .fa {
    font-size: 24px;
    margin: 0 10px 0 5px;
    width: 15px;
    text-align: center;
  }
  @media only screen and (max-width : 649px) {
    margin-left: 10px;
  }
`;

const SectionDetails = styled.div`
  font-weight: normal;
  color: gray;
  font-size: 14px;
  margin-right: 5px;
`;

const WorksheetWrapper = styled.div`
  border-radius: 10px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 350px), 1fr));
  gap: 15px;
  min-height: ${props => props.minHeight};

  & > *:only-child {
    max-width: 575px;
    margin: auto;
  }
`;

const AdminLink = styled.span`
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  margin-right: 20px;
  vertical-align: bottom;
`;

@observer
class GridLayout extends React.Component {
  @observable gridIsLoaded = false;
  @observable searchQuery = null;
  @observable closedSections = [];
  @observable gridSectionIsLoading = {};
  @observable loadedTagCategories = AppUI.tagCategoryStore.hasIndexData;

  constructor(props) {
    super(props);
    if (!AppUI.layout.isMobile) {
      window.addEventListener('scroll', this.onScroll, true);
    }
    if (this.loadedTagCategories) {
      this.getGrid();
    }
    autorun(() => {
      if (!this.loadedTagCategories && AppUI.userIsLoaded) {
        this.loadedTagCategories = true;
        this.loadTags();
      }
    });
  }

  componentDidMount() {
    if (isPresent(ExerciseUI.userGroup) && isPresent(ExerciseUI.userGroup.closedGridSections)) {
      this.closedSections = ExerciseUI.userGroup.closedGridSections.split(',') ;
    }
  }

  componentWillUnmount() {
    ExerciseUI.worksheetStore.clearIndexData();
    window.removeEventListener('scroll', this.onScroll, true);
  }

  @autobind onScroll() {
    const page = ExerciseUI.worksheetStore.page;
    const app = document.getElementsByClassName('app')[0];
    if (!this.useGridLayout &&
        ExerciseUI.worksheetStore.requestCounter === 0 &&
        !ExerciseUI.worksheetStore.indexDataFullyLoaded &&
        isPresent(app)) {
      // Calculate remaining scroll space
      const scrollBottom = app.scrollTop + app.clientHeight;
      const scrollThreshold = app.scrollHeight - 250;

      if (scrollBottom >= scrollThreshold) {
        ExerciseUI.worksheetStore.setPage(page + 1);
        ExerciseUI.worksheetStore.getIndex(
          {
            limit: this.worksheetLimit,
            sort: this.sortParameter,
            filters: this.worksheetFilters,
            queryStrings: {
              show_all: !this.props.showFilters,
              videos: this.props.route.name === 'videos.index',
              podcast: this.props.route.name === 'podcast.index',
              home: this.props.route.name === AppUI.site.signedInHomePageRoute,
            },
          },
          this.afterOnScroll,
        );
      }
    }
  }

  @autobind afterOnScroll(resp) {
    ExerciseUI.worksheetStore.setIndexData(resp, {addToIndexData: true});
  }

  @computed get useGridLayout() {
    return this.props.useGridLayout && (!ExerciseUI.site.showWorksheetFilter || !this.hasSelectedFilterTags && isPresent(AppUI.user.currentUserGroup) && AppUI.tagCategoryStore.hasIndexData && isBlank(this.searchQuery));
  }

  @computed get worksheetFilters() {
    let filters = {};
    if (this.props.defaultWorksheetFilter) {
      filters = this.props.defaultWorksheetFilter;
    }
    if (this.useGridLayout) {
      filters['grid_items_count'] = 0;
    }
    if (!ExerciseUI.user || !ExerciseUI.user.anyAdmin) {
      filters['published'] = true;
    }
    if (this.searchQuery) {
      filters['name'] = `~${this.searchQuery}~`;
    } else if (isBlank(this.searchQuery) && isPresent(filters['name'])) {
      delete filters['name'];
    } else {
      if (this.props.params?.filter === 'video_clips') {
        filters['podcast_episode_id'] = false;
      } else if (this.props.params?.filter === 'podcasts') {
        filters['podcast_episode_id'] = true;
      }
    }
    return filters;
  }

  @autobind onSearchQueryChange(val) {
    this.searchQuery = val;
    clearInterval(this.interval);
    if (isBlank(val)) {
      this.getGrid();
    }
    if ( isBlank(val) || val.length > 1) {
      this.interval = setTimeout(this.getGrid, 500);
    }
  }

  @computed get sortParameter() {
    const showFreeWorksheetsFirst = !ExerciseUI.user.isMember || (AppUI.vocabBlockedUntilMigrated || AppUI.exercisesBlockedUntilMigrated);
    return [
      showFreeWorksheetsFirst && '-access_without_membership',
      '-pinned',
      '-content_published_at',
    ].filter(Boolean).join(',');
  }

  @autobind getGrid(params = {}) {
    this.gridIsLoaded = false;
    if (this.useGridLayout && (!ExerciseUI.gridItemStore.hasIndexData || params.forceLoad)) {
      AppUI.set('worksheetGridScrollPosition', 0);
      ExerciseUI.gridItemStore.clearFilter();
      ExerciseUI.gridItemStore.getIndex({
        queryStrings: {
          sort: 'position',
          filters: {},
          worksheet_filters: this.worksheetFilters,
          show_all: !this.props.showFilters,
        }}, this.getGridItems);
    } else {
      if (this.recentWorksheetIds && this.useGridLayout) {
        params.excludedWorksheetIds = this.recentWorksheetIds;
      }
      this.getWorksheets(params);
    }
  }

  @computed get worksheetLimit() {
    if (this.useGridLayout) {
      return 6;
    } else if (AppUI.layout.isMobile) {
      return 8;
    } else {
      return 15;
    }
  }

  @autobind getWorksheets(params) {
    const filtersHaveChanged = some(keys(this.worksheetFilters)?.map((key => this.worksheetFilters[key] !== ExerciseUI.worksheetStore.getFilterValue(key)))) || some(keys(toJS(ExerciseUI.worksheetStore.filters))?.map((key => this.worksheetFilters[key] !== ExerciseUI.worksheetStore.getFilterValue(key))));
    const sortHasChanged = ExerciseUI.worksheetStore.sort !== this.sortParameter;
    if (!ExerciseUI.worksheetStore.hasIndexData || filtersHaveChanged || sortHasChanged || (isPresent(params) && params['forceLoad'])) {
      AppUI.set('worksheetGridScrollPosition', 0);
      ExerciseUI.worksheetStore.clearIndexData();
      ExerciseUI.worksheetStore.clearFilter();
      ExerciseUI.worksheetStore.getIndex({
        limit: this.worksheetLimit,
        sort: this.sortParameter,
        filters: this.worksheetFilters,
        queryStrings: {
          show_all: !this.props.showFilters,
          excluded_worksheet_ids: params?.excludedWorksheetIds,
          videos: this.props.route.name === 'videos.index',
          podcast: this.props.route.name === 'podcast.index',
          home: this.props.route.name === AppUI.site.signedInHomePageRoute,
      },
      }, this.afterGetWorksheets);
    } else {
      this.gridIsLoaded = true;
    }
  }

  @autobind afterGetWorksheets(resp) {
    ExerciseUI.worksheetStore.setIndexData(resp);
    this.gridIsLoaded = true;
  }

  @autobind getGridItems(resp) {
    ExerciseUI.gridItemStore.setIndexData(resp);
    this.gridIsLoaded = true;
    this.visibleSections = resp.map(item => item.id);
    if (ExerciseUI.gridItemStore.indexData.find(item => item.mostRecent && item.isVisibleToUser(ExerciseUI.user))) {
      ExerciseUI.recentWorksheetStore.getIndex({queryStrings: {show_all: 'true'}}, this.afterGetRecentWorksheets);
    } else {
      this.getWorksheets();
    }
  }

  @autobind afterGetRecentWorksheets(resp) {
    ExerciseUI.recentWorksheetStore.afterGetIndex(resp);
    this.getWorksheets({excludedWorksheetIds: this.recentWorksheetIds});
  }

  @computed get recentWorksheetIds() {
    return ExerciseUI.worksheetNamespace === 'worksheets' && ExerciseUI.recentWorksheetStore.hasIndexData && ExerciseUI.recentWorksheetStore.indexData.map(item => item.id).join(',');
  }

  @autobind onToggleSection(sectionId) {
    this.closedSections = xor(this.closedSections, [sectionId]);
    if (ExerciseUI.userGroup) {
      ExerciseUI.userGroup.set('closedGridSections', this.closedSections.join(','));
    }
    ExerciseUI.userGroupStore.update({ids: {userGroupId: ExerciseUI.userGroup.id}, data: {closed_grid_sections: this.closedSections.join(',')}});
  }

  @autobind sectionIsOpen(sectionId) {
    return !this.closedSections?.includes(sectionId);
  }

  @autobind getGridItemWorksheets(gridItem) {
    ExerciseUI.gridItemWorksheetStore.getIndex({
      queryStrings: {
        filters: {grid_item_id: gridItem.id},
        limit: Constants.WORKSHEETS_PER_SECTION,
        page: gridItem.gridItemState.currentPage,
        worksheet_filters: this.worksheetFilters,
        show_all: !this.props.showFilters,
      }}, resp => this.afterChangeGridItemPage(gridItem.id, resp));
  }

  @autobind onIncrementGridItemPage(increment, gridItem) {
    this.gridSectionIsLoading[gridItem.id] = true;
    gridItem.gridItemState.setPage(gridItem.gridItemState.currentPage + increment);
    this.getGridItemWorksheets(gridItem);
  }

  @autobind afterChangeGridItemPage(gridItemId, resp) {
    this.gridSectionIsLoading[gridItemId] = false;
    if (ExerciseUI.gridItemStore.hasIndexData) {
      ExerciseUI.gridItemStore.indexData.find(item => item.id === gridItemId)['gridItemWorksheets'] = resp.map(item => deserialize(GridItemWorksheet, item));
    }
  }

  @autobind loadTags() {
    AppUI.tagCategoryStore.getIndex({ids: {groupId: AppUI.user.currentUserGroup?.groupId}}, this.afterLoadTags);
  }

  @autobind afterLoadTags(resp) {
    AppUI.tagCategoryStore.setIndexData(resp);
    this.getGrid();
  }

  @autobind displayTagCategory(tagCategory) {
    return (this.props.route?.name === 'videos.index' && tagCategory.displayOnVideoFilters) || (this.props.route?.name === 'podcast.index' && tagCategory.displayOnPodcastFilters) || (tagCategory.displayOnHomeFilters && this.props.route?.name === AppUI.site.signedInHomePageRoute);
  }

  @computed get showProgressTag() {
    const progressCategory = AppUI.tagCategoryStore.indexData.find(item => item.slug === 'progress' && item.published);
    if (isBlank(progressCategory)) { return; }
    return this.displayTagCategory(progressCategory) && AppUI.userIsSignedIn && !AppUI.user.guest;
  }

  @computed get filteredTagCategories() {
    return AppUI.tagCategoryStore.indexData?.filter(item => item.published && item.isWorksheetTag && this.displayTagCategory(item));
  }

  @computed get hasSelectedFilterTags() {
    return (this.showProgressTag && isPresent(AppUI.user.currentUserGroup) && isPresent(AppUI.user.currentUserGroup?.worksheetProgressTags)) || (isPresent(AppUI.user.currentUserGroup?.worksheetTagIds) && some(this.filteredTagCategories.map(tagCategory => some(tagCategory.tags.map(tag => AppUI.user.currentUserGroup?.worksheetTagIds?.split(',').includes(tag.id))))));
  }

  render() {
    return (
      <Wrapper id='page-top' paddingTop={AppUI.layout.isMobile ? '15px' : null}>
        <Stack gap='sm' alignItems='center'>
          {
            this.props.showFilters && ExerciseUI.site.showWorksheetFilter &&
              <WorksheetFilter
                onUpdateFilter={() => this.getGrid({forceLoad: true})}
                onSearchQueryChange={this.onSearchQueryChange}
                searchQuery={this.searchQuery}
                routeName={this.props.route?.name}
                showProgressTag={this.showProgressTag}
                filteredTagCategories={this.filteredTagCategories}
                allTagCategories={AppUI.tagCategoryStore.indexData}
                activeView={this.props.activeView}
                onToggleExerciseView={this.props.onToggleExerciseView}
              />
          }
          {
            this.useGridLayout && !this.gridIsLoaded &&
              <Spinner
                background={ExerciseUI.site.accentColor}
              />
          }
          {
            this.useGridLayout && ExerciseUI.gridItemStore.indexData.map(gridItem => {
              if (gridItem.isVisibleToUser(ExerciseUI.user) && (gridItem.worksheetsCount > 0 || (gridItem.otherPosts && ExerciseUI.worksheetStore.hasIndexData) || (gridItem.mostRecent && ExerciseUI.recentWorksheetStore.hasIndexData))) {
                return (
                  <Section
                    key={gridItem.id}
                  >
                    <Name
                      onClick={() => this.onToggleSection(gridItem.id)}
                      id={`${gridItem.id}-page-top`}
                    >
                      <div>
                        <i className={`fa fa-angle-${this.sectionIsOpen(gridItem.id) ? 'down' : 'right'}`} />
                        {gridItem.name}
                      </div>
                      {
                        !gridItem.isAutomatic &&
                          <SectionDetails>
                            {
                              !AppUI.siteIsDefault && ExerciseUI.user.anyAdmin && AppUI.user.currentUserGroup?.groupId &&
                              <AdminLink>
                                <Link
                                  to={{
                                    name: 'creator.grid_items.edit',
                                    params: {gridItemId: gridItem.id, groupId: AppUI.user.currentUserGroup?.groupId},
                                  }}
                                >
                                  Admin Link
                                </Link>
                              </AdminLink>
                            }
                            {
                              isPresent(gridItem.filteredWorksheetsCount) &&
                              `${gridItem.filteredWorksheetsCount} Post${gridItem.filteredWorksheetsCount === 0 ? '' : 's'}`
                            }
                          </SectionDetails>
                      }
                    </Name>
                    {
                      this.sectionIsOpen(gridItem.id) &&
                        <WorksheetWrapper
                          id={gridItem.id}
                          minHeight={gridItem.gridItemWorksheets.length > 3 ? 600 : null}
                        >
                          {
                            !gridItem.isAutomatic && !this.gridSectionIsLoading[gridItem.id] && gridItem.gridItemWorksheets.map(gridItemWorksheet => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={gridItemWorksheet.id}
                                  worksheet={gridItemWorksheet.worksheet}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                />
                              );
                            })
                          }
                          {
                            gridItem.mostRecent && ExerciseUI.recentWorksheetStore.hasIndexData && ExerciseUI.recentWorksheetStore.indexData.map(item => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={item.id}
                                  worksheet={item}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                />
                              );
                            })
                          }
                          {
                            gridItem.otherPosts && ExerciseUI.worksheetStore.indexData.map(item => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={item.id}
                                  worksheet={item}
                                  gridLayout={this.useGridLayout}
                                />
                              );
                            })
                          }
                          {
                            this.gridSectionIsLoading[gridItem.id] && [...Array(6)].map((item, index) => {
                              return (
                                <WorksheetGridItem
                                  onUpdateFilter={() => this.getGrid({forceLoad: true})}
                                  key={`placeholder-${index}`}
                                  gridLayout={this.useGridLayout}
                                  hidePinIcon
                                />
                              );
                            })
                          }
                        </WorksheetWrapper>
                    }
                    {
                      !gridItem.isAutomatic && this.sectionIsOpen(gridItem.id) && gridItem.filteredWorksheetsCount > Constants.WORKSHEETS_PER_SECTION &&
                        <Paginator
                          onClickScrollToTop={AppUI.layout.isMobile}
                          pageTop={`${gridItem.id}-page-top`}
                          hideInput
                          onPagination={increment => this.onIncrementGridItemPage(increment, gridItem)}
                          totalCount={gridItem.filteredWorksheetsCount}
                          currentPage={gridItem.gridItemState.currentPage}
                          limit={Constants.WORKSHEETS_PER_SECTION}
                        />
                    }
                    {
                      gridItem.otherPosts && this.sectionIsOpen(gridItem.id) &&
                        <Paginator
                          onClickScrollToTop={AppUI.layout.isMobile}
                          store={ExerciseUI.worksheetStore}
                          pageTop={`${gridItem.id}-page-top`}
                          queryStrings={{excluded_worksheet_ids: this.recentWorksheetIds, show_all: true}}
                        />
                    }
                  </Section>
                );
              }
            })
          }
          {
            !this.useGridLayout && ExerciseUI.worksheetStore.hasIndexData &&
              <Section>
                <WorksheetWrapper>
                  {
                    ExerciseUI.worksheetStore.indexData.map(item => {
                      return (
                        <WorksheetGridItem
                          onUpdateFilter={() => this.getGrid({forceLoad: true})}
                          key={item.id}
                          worksheet={item}
                          gridLayout={this.useGridLayout}
                        />
                      );
                    })
                  }
                </WorksheetWrapper>
                {
                  AppUI.layout.isMobile &&
                    <Paginator
                      onClickScrollToTop
                      pageTop='top'
                      store={ExerciseUI.worksheetStore}
                    />
                }
              </Section>
          }
          {
            !this.useGridLayout && ExerciseUI.worksheetStore.requestCounter > 0 &&
              <Spinner
                background={ExerciseUI.site.accentColor}
              />
          }
          {
            ExerciseUI.site.showWorksheetFilter && this.gridIsLoaded && !this.useGridLayout && ExerciseUI.worksheetStore.indexData?.length === 0 &&
              <Text center>Sorry, we don't have any post that match these filters!</Text>
          }
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(GridLayout);
